import * as React from 'react';
import { navigate } from 'mydish-ui-util';
import { useHistory } from 'react-router-dom';
import NavButton from './NavButton';
import SearchInput from './SearchInput';
import StaticElementButton from './StaticElementButton';

export interface ChildPage {
  name: string;
  href: string;
  pageName: string;
  pageURL: string;
}

export interface SectionPage {
  name?: string;
  childPages: ChildPage[];
  collapsed?: boolean;
}

export interface EvergreenLink {
  id: string;
  name: string;
  href: string;
}

interface DrawerProps {
  sectionPages?: SectionPage[];
  selectedDrawer?: string;
  onBlur: Function;
  navOptions?: React.ReactNode[];
  navSelected?: boolean;
  selectedNavName?: string;
  toggleMenuOpen?: boolean;
  backToMenu: Function;
  navEvergreenLinks?: EvergreenLink[];
  collapsibleHeaders?: boolean;
  enablePrimaryLinkNavigation?: boolean;
}

interface DrawerState {
  activeDrawerLinkName?: string;
  toggleMenu?: boolean;
}

export default function DrawerComponent(props: DrawerProps) {
  const [state, setState] = React.useState<DrawerState>({
    activeDrawerLinkName: props.selectedDrawer
  });

  const [sectionPages, setSectionPages] = React.useState<
    SectionPage[] | undefined
  >();

  const history = useHistory();

  let drawerLinkIndex: number = -1;
  const node: any = React.useRef(null);

  const handleHideDrawerOnEsc = (event: any) => {
    if (event.key === 'Escape') {
      props.onBlur();
    }
  };
  const handleClickOutside = (event: any) => {
    if (node.current.contains(event.target)) {
      return;
    }
    // outside click
    props.onBlur();
  };
  const handleLastLinkFocus = (event: any) => {
    const drawerContentLinks = node.current.querySelectorAll(
      '.drawer-content-link'
    );
    if (drawerContentLinks[drawerContentLinks.length - 1] === event.target) {
      drawerContentLinks[0].focus();
      event.preventDefault();
    }
  };
  const navSectionsRef: any = React.useRef(null);

  const handleMouseDown = () => {
    if (navSectionsRef.current) {
      navSectionsRef.current.classList.add('using-mouse');
    }
  };
  const handleKeyDown = (e: any) => {
    if (navSectionsRef.current) {
      if (e.keyCode === 9) {
        navSectionsRef.current.classList.remove('using-mouse');
      }
    }
  };

  React.useEffect(() => {
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
  }, [props]);

  React.useEffect(() => {
    setState({ ...state, activeDrawerLinkName: props.selectedDrawer });
    const drawerContentLinks = node.current.querySelectorAll(
      '.drawer-content-link'
    );
    if (drawerContentLinks && drawerContentLinks.length) {
      drawerContentLinks[0].focus();
    }
    if (props.toggleMenuOpen || props.navSelected) {
      document.addEventListener('keydown', handleHideDrawerOnEsc);
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('keydown', handleHideDrawerOnEsc);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
    return undefined;
  }, [props.toggleMenuOpen, props.navSelected]);
  let keyIndex = 0;

  React.useEffect(() => {
    setSectionPages(props.sectionPages);
  }, [props.sectionPages]);

  return (
    <div
      className={`drawer ${state.toggleMenu ? 'toggleMenu ' : ''}${
        props.toggleMenuOpen || props.navSelected ? 'slide-in' : 'slide-out'
      } ${
        !props.navSelected && props.navOptions !== undefined ? 'default' : ''
      }`}
      ref={node}
    >
      {props.navOptions !== undefined && (
        <div className="mobileNavOptions">
          {!props.enablePrimaryLinkNavigation && (
            <div className="nav-search-field">
              <SearchInput
                focus
                onSearch={(searchText: string) => {
                  navigate(`/search?q=${searchText}`, history);
                }}
                name="globalSearch"
                id="globalSearch"
                label="Search"
                theme="night"
              />
            </div>
          )}
          {props.navOptions.map((navOption: React.ReactNode) => navOption)}
        </div>
      )}

      <div className="navOptions">
        {props.navSelected && (
          <button
            className="backBtn"
            type="button"
            aria-label="back menu"
            onClick={() => props.backToMenu()}
          >
            <span className="arrow-left" />
            {props.selectedNavName}
          </button>
        )}
        <div className="navSections" ref={navSectionsRef}>
          {props.navSelected &&
            sectionPages &&
            sectionPages.map((sectionPage: SectionPage, index: number) => {
              if (!sectionPage) return null;
              keyIndex += 1;
              return (
                <div
                  key={`${(sectionPage.name || 'sectionPage')
                    .toLowerCase()
                    .replaceAll(' ', '')}${keyIndex}`}
                  className={`navSection${
                    props.collapsibleHeaders ? ' collapsibleNav' : ''
                  }`}
                >
                  {sectionPage.name && (
                    <div className="sectionPage">
                      {props.collapsibleHeaders ? (
                        <button
                          type="button"
                          onClick={() => {
                            const updatedPages = [...sectionPages];
                            updatedPages[index].collapsed =
                              !updatedPages[index].collapsed;
                            setSectionPages(updatedPages);
                          }}
                        >
                          <h2 className="sectionTitle">{sectionPage.name}</h2>
                          <div
                            className={`navDropdownArrow${
                              sectionPage.collapsed ||
                              sectionPage.collapsed === undefined
                                ? ' collapsedArrow'
                                : ' expandedArrow'
                            }`}
                          />
                        </button>
                      ) : (
                        <h2 className="sectionTitle">{sectionPage.name}</h2>
                      )}
                    </div>
                  )}
                  {keyIndex === 1 && (
                    <StaticElementButton
                      className="closeDrawerIcon"
                      id="close-drawer"
                      name="close-drawer"
                      label=""
                      tabIndex={0}
                      type="div"
                      onClick={() => props.onBlur()}
                    />
                  )}
                  <div
                    className={`navSectionChildren${
                      props.collapsibleHeaders &&
                      sectionPage.collapsed !== undefined
                        ? ` ${
                            sectionPage.collapsed
                              ? 'collapsedNav'
                              : 'expandedNav'
                          }`
                        : ''
                    }`}
                    id={`navSectionChildren-${keyIndex}`}
                  >
                    {sectionPage.childPages.map((childPage: ChildPage) => {
                      if (!sectionPage) return null;
                      const pageName = childPage.name || childPage.pageName;
                      const href = childPage.href || childPage.pageURL;
                      drawerLinkIndex += 1;
                      keyIndex += 1;
                      return (
                        <NavButton
                          id={`drawer-content-link-${drawerLinkIndex}`}
                          label={pageName}
                          className="drawer-content-link"
                          key={`${pageName
                            .toUpperCase()
                            .replaceAll(' ', '')}${keyIndex}`}
                          disabled={
                            props.collapsibleHeaders && sectionPage.collapsed
                          }
                          onClick={() => {
                            setState({
                              ...state,
                              activeDrawerLinkName: pageName
                            });
                            navigate(href, history);
                          }}
                          onBlur={handleLastLinkFocus}
                          active={pageName === state.activeDrawerLinkName}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
      <div className="evergreenlinks">
        {props.navEvergreenLinks &&
          props.navEvergreenLinks.map((evergreenlink: EvergreenLink) => {
            if (!evergreenlink) return null;
            keyIndex += 1;
            return evergreenlink.id === 'eg-feedback' ? (
              <NavButton
                key={`${evergreenlink.name
                  .toLowerCase()
                  .replaceAll(' ', '')}${keyIndex}`}
                id={evergreenlink.id}
                label={evergreenlink.name}
                className="evergreen-link"
                onClick={() => {
                  if (
                    document.getElementById('provideFeedbackButton') !== null
                  ) {
                    // @ts-ignore
                    document.getElementById('provideFeedbackButton').click();
                  }
                }}
              />
            ) : (
              <NavButton
                id={evergreenlink.id}
                key={`${evergreenlink.name
                  .toLowerCase()
                  .replaceAll(' ', '')}${keyIndex}`}
                label={evergreenlink.name}
                className="evergreen-link"
                onClick={() => {
                  navigate(evergreenlink.href, history);
                }}
              />
            );
          })}
      </div>
    </div>
  );
}
